import { useEffect, useRef, useState } from "react";
import { useQuery, useMutation, keepPreviousData } from "@tanstack/react-query";
import { format } from "date-fns";
import { ArrowDown, ArrowUp, ArrowUpDown, FileText, Loader2, RefreshCw, Search, X } from "lucide-react";
import AdminLayout from "./AdminLayout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { useDebounce } from "@/hooks/use-debounce";
import { ApiError, apiRequest, queryClient } from "@/lib/queryClient";

/**
 * AR-379: Content Management — the posts mirrored from PRO (Bubble) by the
 * AR-378 sync. Search, filters, sort and paging all run on the server.
 */

const API = "/api/admin/pro-content";
const POSTS_KEY = `${API}/posts`;
const STATUS_KEY = `${API}/sync/status`;
const ALL = "all";
const PAGE_SIZE = 25;

interface PostRow {
  uniqueId: string;
  title: string | null;
  postType: string | null;
  status: string | null;
  modifiedDate: string | null;
  syncedAt: string | null;
  missingFromBubbleAt: string | null;
}

interface PostsResponse {
  items: PostRow[];
  total: number;
  page: number;
  pageSize: number;
}

interface FacetOption {
  value: string;
  label: string | null;
  count: number;
}

interface TableStats {
  sourceTotal: number;
  fetched: number;
  complete: boolean;
}

interface SyncRun {
  id: number;
  scope: "full" | "incremental" | "single";
  status: "running" | "succeeded" | "failed";
  trigger: "schedule" | "admin";
  triggeredByName: string | null;
  startedAt: string;
  finishedAt: string | null;
  createdCount: number;
  updatedCount: number;
  missingCount: number;
  failedCount: number;
  tableStats: Record<string, TableStats> | null;
  error: string | null;
}

interface Schedule {
  enabled: boolean;
  cron: string;
  timeZone: string;
}

interface SyncStatus {
  running: boolean;
  current: SyncRun | null;
  lastRun: SyncRun | null;
  schedule: Schedule;
  incrementalSchedule?: Schedule;
}

type SortField = "title" | "postType" | "status";

const formatDateTime = (value: string | null) =>
  value ? format(new Date(value), "MMM d, yyyy h:mm a") : "—";

const changedCount = (run: SyncRun) => run.createdCount + run.updatedCount + run.missingCount;

const plural = (count: number, word: string) =>
  `${count.toLocaleString()} ${word}${count === 1 ? "" : "s"}`;

const runSummary = (run: SyncRun) =>
  `${plural(changedCount(run), "record")} changed` +
  ` (${run.createdCount.toLocaleString()} new, ${run.updatedCount.toLocaleString()} updated,` +
  ` ${run.missingCount.toLocaleString()} removed from PRO)` +
  (run.failedCount ? `, ${plural(run.failedCount, "record")} failed` : "");

/** "0 3 * * *" → "daily at 3:00 AM"; anything fancier is shown as the cron itself. */
const describeSchedule = ({ enabled, cron, timeZone }: Schedule) => {
  if (!enabled) return "Nightly sync is off";
  const match = cron.match(/^(\d{1,2}) (\d{1,2}) \* \* \*$/);
  if (!match) return `Scheduled: ${cron} (${timeZone})`;
  const time = format(new Date(2000, 0, 1, Number(match[2]), Number(match[1])), "h:mm a");
  return `Runs daily at ${time} (${timeZone})`;
};

/** "*\/30 * * * *" → "changed posts every 30 minutes". */
const describeIncremental = ({ enabled, cron }: Schedule) => {
  if (!enabled) return "changes-only sync is off";
  const match = cron.match(/^\*\/(\d{1,2}) \* \* \* \*$/);
  return match ? `changed posts every ${match[1]} minutes` : `changed posts on "${cron}"`;
};

/** Who or what started a run. */
const runSource = (run: SyncRun, admin: string) =>
  run.scope === "incremental"
    ? "changes-only sync"
    : run.trigger === "schedule"
      ? "nightly schedule"
      : run.triggeredByName ?? admin;

const progressText = (run: SyncRun) => {
  const stats = Object.entries(run.tableStats ?? {});
  const active = stats.find(([, s]) => !s.complete) ?? stats[stats.length - 1];
  if (!active) return "Starting…";
  const [type, s] = active;
  const progress = `${type}: ${s.fetched.toLocaleString()} of ${s.sourceTotal.toLocaleString()}`;
  if (run.scope === "incremental") return `${progress} changed posts`;
  return `${progress} · ${stats.filter(([, t]) => t.complete).length} of 11 types done`;
};

const errorText = (error: unknown) =>
  error instanceof Error ? error.message : "Something went wrong";

const RESYNC_MESSAGES: Record<string, string> = {
  created: "Added from PRO",
  updated: "Updated from PRO",
  unchanged: "Already up to date",
  missing: "No longer in PRO — marked as removed",
  failed: "Could not be synced",
};

export default function AdminContentPage() {
  return (
    <AdminLayout
      title="Content Management"
      description="Posts synced from WorldVia PRO. Sync everything, or resync a single post that looks out of date."
    >
      <ContentSync />
    </AdminLayout>
  );
}

function ContentSync() {
  const { toast } = useToast();
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearch = useDebounce(searchQuery, 300).trim();
  const [postType, setPostType] = useState(ALL);
  const [status, setStatus] = useState(ALL);
  const [sort, setSort] = useState<{ by: SortField; dir: "asc" | "desc" }>({ by: "title", dir: "asc" });
  const [resyncing, setResyncing] = useState<Set<string>>(() => new Set());

  // Any change to search, filters or sort starts again at page 1.
  const filterKey = JSON.stringify([debouncedSearch, postType, status, sort.by, sort.dir]);
  const [paging, setPaging] = useState({ key: filterKey, page: 1 });
  const page = paging.key === filterKey ? paging.page : 1;

  const params = new URLSearchParams({
    page: String(page),
    pageSize: String(PAGE_SIZE),
    sortBy: sort.by,
    sortDir: sort.dir,
  });
  if (debouncedSearch) params.set("search", debouncedSearch);
  if (postType !== ALL) params.set("postType", postType);
  if (status !== ALL) params.set("status", status);
  const postsKey = [POSTS_KEY, params.toString()];

  const postsQuery = useQuery({
    queryKey: postsKey,
    queryFn: (): Promise<PostsResponse> => apiRequest("GET", `${POSTS_KEY}?${params.toString()}`),
    placeholderData: keepPreviousData,
  });
  const facetsQuery = useQuery({
    queryKey: [`${POSTS_KEY}/facets`],
    queryFn: (): Promise<{ postTypes: FacetOption[]; statuses: FacetOption[] }> =>
      apiRequest("GET", `${POSTS_KEY}/facets`),
  });
  // Every admin on the page sees a running sync, whoever started it.
  const statusQuery = useQuery({
    queryKey: [STATUS_KEY],
    queryFn: (): Promise<SyncStatus> => apiRequest("GET", STATUS_KEY),
    refetchInterval: (query) => (query.state.data?.running ? 3000 : 15000),
  });

  const syncStatus = statusQuery.data;
  const running = Boolean(syncStatus?.running);

  // When a full run this page watched finishes, report it and reload the table.
  // The 30-minute changes-only runs finish quietly.
  const watchedRunId = useRef<number | null>(null);
  useEffect(() => {
    if (syncStatus?.running && syncStatus.current) {
      if (syncStatus.current.scope === "full") watchedRunId.current = syncStatus.current.id;
      return;
    }
    const finished = syncStatus?.lastRun;
    if (watchedRunId.current === null || !finished || finished.id !== watchedRunId.current) return;
    watchedRunId.current = null;
    void queryClient.invalidateQueries({ queryKey: [POSTS_KEY] });
    toast(
      finished.status === "succeeded"
        ? { title: "Sync complete", description: runSummary(finished) }
        : {
            title: "Sync finished with errors",
            description: `${runSummary(finished)}. ${finished.error ?? ""}`.trim(),
            variant: "destructive",
          },
    );
  }, [syncStatus, toast]);

  const syncAll = useMutation({
    mutationFn: (): Promise<{ run: SyncRun }> => apiRequest("POST", `${API}/sync`),
    onSuccess: ({ run }) => {
      watchedRunId.current = run.id;
      queryClient.setQueryData<SyncStatus>([STATUS_KEY], (prev) =>
        prev ? { ...prev, running: true, current: run } : prev,
      );
      void queryClient.invalidateQueries({ queryKey: [STATUS_KEY] });
    },
    onError: (error) => {
      void queryClient.invalidateQueries({ queryKey: [STATUS_KEY] });
      toast({
        title: error instanceof ApiError && error.status === 409 ? "A sync is already running" : "Could not start the sync",
        description: errorText(error),
        variant: "destructive",
      });
    },
  });

  const resync = useMutation({
    mutationFn: (uniqueId: string): Promise<{ outcome: string; reason?: string; row: PostRow | null }> =>
      apiRequest("POST", `${API}/sync/post/${encodeURIComponent(uniqueId)}`),
    onMutate: (uniqueId) => setResyncing((prev) => new Set(prev).add(uniqueId)),
    onSettled: (_data, _error, uniqueId) =>
      setResyncing((prev) => {
        const next = new Set(prev);
        next.delete(uniqueId);
        return next;
      }),
    onSuccess: ({ outcome, reason, row }, uniqueId) => {
      if (row) {
        queryClient.setQueriesData<PostsResponse>({ queryKey: [POSTS_KEY] }, (prev) =>
          prev?.items ? { ...prev, items: prev.items.map((item) => (item.uniqueId === uniqueId ? row : item)) } : prev,
        );
      }
      toast({
        title: RESYNC_MESSAGES[outcome] ?? "Resynced",
        description: [row?.title ?? uniqueId, reason].filter(Boolean).join(" — "),
        variant: outcome === "failed" ? "destructive" : undefined,
      });
    },
    onError: (error) =>
      toast({ title: "Resync failed", description: errorText(error), variant: "destructive" }),
  });

  const onResync = (uniqueId: string) => {
    if (running || resyncing.has(uniqueId)) return;
    resync.mutate(uniqueId);
  };

  const toggleSort = (by: SortField) =>
    setSort((prev) => ({ by, dir: prev.by === by && prev.dir === "asc" ? "desc" : "asc" }));

  const items = postsQuery.data?.items ?? [];
  const total = postsQuery.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const filtered = Boolean(debouncedSearch) || postType !== ALL || status !== ALL;
  const syncAllDisabled = running || syncAll.isPending || statusQuery.isLoading;

  const clearFilters = () => {
    setSearchQuery("");
    setPostType(ALL);
    setStatus(ALL);
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-4 space-y-0">
          <div className="space-y-1">
            <CardTitle className="flex items-center gap-2">
              <RefreshCw className="h-5 w-5" />
              Sync from PRO
            </CardTitle>
            <CardDescription>
              {syncStatus
                ? describeSchedule(syncStatus.schedule) +
                  (syncStatus.incrementalSchedule ? `, plus ${describeIncremental(syncStatus.incrementalSchedule)}` : "")
                : "Loading sync status…"}
            </CardDescription>
          </div>
          <Button onClick={() => syncAll.mutate()} disabled={syncAllDisabled} aria-busy={running || syncAll.isPending}>
            {running || syncAll.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="mr-2 h-4 w-4" />
            )}
            {running ? "Sync running…" : "Sync All"}
          </Button>
        </CardHeader>
        <CardContent className="space-y-2 text-sm" role="status" aria-live="polite">
          {running && syncStatus?.current && (
            <p className="text-slate-700 dark:text-slate-300">
              Sync in progress, started {formatDateTime(syncStatus.current.startedAt)} by{" "}
              {syncStatus.current.scope === "incremental" || syncStatus.current.trigger === "schedule" ? "the " : ""}
              {runSource(syncStatus.current, "an admin")}. {progressText(syncStatus.current)}
            </p>
          )}
          {syncStatus?.lastRun ? (
            <p className="text-muted-foreground">
              Last sync: {formatDateTime(syncStatus.lastRun.finishedAt ?? syncStatus.lastRun.startedAt)}
              {" · "}
              {runSource(syncStatus.lastRun, "admin")}
              {" · "}
              {syncStatus.lastRun.status === "failed" ? (
                <span className="text-red-600 dark:text-red-400">
                  failed: {syncStatus.lastRun.error ?? "unknown error"}
                </span>
              ) : (
                runSummary(syncStatus.lastRun)
              )}
            </p>
          ) : (
            syncStatus && <p className="text-muted-foreground">No sync has run yet.</p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="space-y-4">
          <div>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Posts ({total.toLocaleString()})
            </CardTitle>
            <p className="sr-only" role="status" aria-live="polite">
              {plural(total, "post")}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative w-full sm:w-72">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                aria-label="Search posts by title"
                placeholder="Search by title..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 pr-9"
              />
              {searchQuery && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="absolute right-1 top-1/2 h-6 w-6 -translate-y-1/2 p-0 hover:bg-muted"
                  aria-label="Clear search"
                  onClick={() => setSearchQuery("")}
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>
            <FacetSelect
              label="Post type"
              allLabel="All post types"
              value={postType}
              onChange={setPostType}
              options={facetsQuery.data?.postTypes}
              emptyLabel="(No post type)"
            />
            <FacetSelect
              label="Status"
              allLabel="All statuses"
              value={status}
              onChange={setStatus}
              options={facetsQuery.data?.statuses}
              emptyLabel="(No status)"
            />
            {filtered && (
              <Button variant="link" size="sm" onClick={clearFilters}>
                Clear filters
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border">
            <Table aria-label="Posts" className="table-fixed [&_th]:px-2 [&_td]:px-2">
              <TableHeader>
                <TableRow>
                  <SortableHead field="title" label="Title" sort={sort} onSort={toggleSort} />
                  <SortableHead field="postType" label="Post type" sort={sort} onSort={toggleSort} className="w-[22%]" />
                  <SortableHead field="status" label="Status" sort={sort} onSort={toggleSort} className="w-[18%]" />
                  <TableHead className="w-[112px] text-right">
                    <span className="sr-only">Resync</span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {postsQuery.isLoading ? (
                  <TableRow>
                    <TableCell colSpan={4} className="h-32 text-center">
                      <Loader2 className="mx-auto h-6 w-6 animate-spin text-muted-foreground" />
                    </TableCell>
                  </TableRow>
                ) : postsQuery.error ? (
                  <TableRow>
                    <TableCell colSpan={4} className="h-32 text-center text-red-600">
                      Error loading posts: {errorText(postsQuery.error)}
                    </TableCell>
                  </TableRow>
                ) : items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} className="h-32 text-center text-muted-foreground">
                      {filtered ? (
                        <div className="flex flex-col items-center gap-2">
                          <span>No posts match these filters</span>
                          <Button variant="link" size="sm" onClick={clearFilters}>
                            Clear filters
                          </Button>
                        </div>
                      ) : (
                        "No posts synced yet. Run Sync All to pull them from PRO."
                      )}
                    </TableCell>
                  </TableRow>
                ) : (
                  items.map((post) => (
                    <TableRow key={post.uniqueId}>
                      <TableCell className="truncate font-medium" title={post.title ?? undefined}>
                        {post.title ?? <span className="text-muted-foreground">(untitled)</span>}
                      </TableCell>
                      <TableCell className="truncate">{post.postType ?? <span className="text-muted-foreground">—</span>}</TableCell>
                      <TableCell>
                        <div className="flex flex-wrap items-center gap-1">
                          {post.status ?? <span className="text-muted-foreground">—</span>}
                          {post.missingFromBubbleAt && (
                            <Badge variant="outline" className="border-amber-500 text-amber-700 dark:text-amber-400">
                              Removed from PRO
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => onResync(post.uniqueId)}
                          disabled={running || resyncing.has(post.uniqueId)}
                          aria-label={`Resync ${post.title ?? post.uniqueId}`}
                          aria-busy={resyncing.has(post.uniqueId)}
                        >
                          {resyncing.has(post.uniqueId) ? (
                            <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <RefreshCw className="mr-1 h-3.5 w-3.5" />
                          )}
                          Resync
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
          <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
            <div className="text-sm text-muted-foreground">
              Page {page} of {totalPages} · Showing {total ? (page - 1) * PAGE_SIZE + 1 : 0}-
              {Math.min(page * PAGE_SIZE, total)} of {total.toLocaleString()}
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPaging({ key: filterKey, page: Math.max(1, page - 1) })}
                disabled={page <= 1 || postsQuery.isFetching}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPaging({ key: filterKey, page: Math.min(totalPages, page + 1) })}
                disabled={page >= totalPages || postsQuery.isFetching}
              >
                Next
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function SortableHead({
  field,
  label,
  sort,
  onSort,
  className,
}: {
  field: SortField;
  label: string;
  sort: { by: SortField; dir: "asc" | "desc" };
  onSort: (field: SortField) => void;
  className?: string;
}) {
  const active = sort.by === field;
  const Icon = !active ? ArrowUpDown : sort.dir === "asc" ? ArrowUp : ArrowDown;
  return (
    <TableHead
      className={className}
      aria-sort={active ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}
    >
      <Button variant="ghost" size="sm" className="-ml-2 h-8 px-2" onClick={() => onSort(field)}>
        {label}
        <Icon className={`ml-1 h-3.5 w-3.5 ${active ? "" : "text-muted-foreground"}`} />
      </Button>
    </TableHead>
  );
}

function FacetSelect({
  label,
  value,
  onChange,
  options,
  emptyLabel,
  allLabel,
}: {
  label: string;
  allLabel: string;
  value: string;
  onChange: (value: string) => void;
  options?: FacetOption[];
  emptyLabel: string;
}) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="w-full sm:w-52" aria-label={`Filter by ${label.toLowerCase()}`}>
        <SelectValue placeholder={label} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={ALL}>{allLabel}</SelectItem>
        {options?.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {option.label ?? emptyLabel} ({option.count.toLocaleString()})
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
